/** A global, headless player */
import { logger, shortUuid } from '@tunasong/models'
import { useEffect, useRef, useState } from 'react'
import type { IAudioContext, IMediaElementAudioSourceNode } from 'standardized-audio-context'
import { useMixer } from '../hooks/mixer.hook.js'
import { useGlobalPlayerState } from './global-player-state.hook.js'
import { useGlobalPlayer } from './global-player.hook.js'

/**
 * The one controller every audio link plays through (see GlobalPlayerCommands): links call the registered
 * commands instead of each running `useGlobalPlayer`. The commands are stable and always call this render's
 * controller, so they work from the click handler that needs the user gesture to start audio. Renders nothing.
 */
const GlobalPlayerCommandsBridge = () => {
  const { playlist, controls } = useGlobalPlayer()
  const setCommands = useGlobalPlayerState(state => state.setCommands)
  const latest = useRef({ playNow: playlist.playNow, pause: controls.pause })

  useEffect(() => {
    latest.current = { playNow: playlist.playNow, pause: controls.pause }
  })

  useEffect(() => {
    setCommands({
      playNow: (media, sourceEntityId) => void latest.current.playNow(media, sourceEntityId),
      pause: () => latest.current.pause(),
    })
    return () => setCommands(null)
  }, [setCommands])

  return null
}

export const GlobalAudioPlayer = () => {
  /**
   * Select only what is rendered or called. A whole-store subscription re-rendered this component on its own
   * `setPlayerRef` write, and every other player-store change (GH #1549).
   */
  const currentSrc = useGlobalPlayerState(s => s.currentSrc)
  const setPlayerRef = useGlobalPlayerState(s => s.setPlayerRef)
  const setPlaying = useGlobalPlayerState(s => s.setPlaying)

  const [id] = useState(() => shortUuid())
  const mixer = useMixer()

  /**
   * The <audio> element is held in a ref, not state: it is attached before the effect below runs, so the
   * channel is set up in the mount commit instead of in a re-render triggered by the ref (GH #1549).
   */
  const audioRef = useRef<HTMLAudioElement>(null)
  const elSource = useRef<IMediaElementAudioSourceNode<IAudioContext> | null>(null)
  const channelId = useRef<string>(null)

  // Register the channel in the mixer
  useEffect(() => {
    const audioEl = audioRef.current
    if (!audioEl) {
      return
    }

    const removeChannel = () => {
      if (!channelId.current) {
        return
      }

      /**
       * We disconnect the MediaElementSource from downstream nodes but keep the
       * reference around. Per the Web Audio spec an HTMLMediaElement may be bound
       * to a MediaElementSourceNode only ONCE for its lifetime — re-creating one
       * after a teardown throws InvalidStateError ("HTMLMediaElement already
       * connected previously to a different MediaElementSourceNode"). Reusing the
       * existing source on the next setup is the only safe path. See GH #1181.
       */
      if (elSource.current) {
        try {
          elSource.current.disconnect()
        } catch (e) {
          // Handle possible errors if already disconnected
          logger.debug('Error disconnecting source', e)
        }
      }

      mixer.removeChannel(channelId.current)
      channelId.current = null
    }

    const newChannelId = `audioplayer-${shortUuid()}`

    logger.debug('Setting up channel', { id: newChannelId, channelId: channelId.current, elSource, audioEl })
    // Remove previous channel
    removeChannel()

    channelId.current = newChannelId

    /**
     * If we already created a MediaElementSource for this <audio>, reuse it.
     * The Web Audio API forbids creating a second MediaElementSource for the
     * same HTMLMediaElement (throws InvalidStateError). On hot reload the
     * AudioEngine — and therefore the mixer's context — can change while the
     * <audio> DOM node survives, so the old source belongs to a disposed
     * context and binding a fresh one against the new context will fail. Catch
     * that case so HMR doesn't crash the React tree under the error boundary
     * (GH #1181). A full reload restores audio.
     */
    let source = elSource.current
    if (!source) {
      try {
        source = mixer.context.createMediaElementSource(audioEl)
        elSource.current = source
      } catch (e) {
        logger.warn(
          'GlobalAudioPlayer: cannot create MediaElementSource — element is already bound (likely after hot reload). Reload the page to restore audio.',
          e
        )
        return removeChannel
      }
    }

    mixer.addChannel(
      {
        id: newChannelId,
        name: 'Global Audio Player',
        type: 'general',
        // gain: volume,
        inputNode: source,
      },
      'media'
    )
    logger.debug('Channel is set up', { id: newChannelId, channelId: channelId.current, elSource, audioEl })
    // Update the global player reference
    setPlayerRef(audioEl)

    return removeChannel
  }, [mixer, setPlayerRef])

  /** Publish whether the shared element plays, from its own events, so play buttons can follow it cheaply. */
  useEffect(() => {
    const audioEl = audioRef.current
    if (!audioEl) {
      return
    }
    const handlePlay = () => setPlaying(true)
    const handleStop = () => setPlaying(false)
    const stopEvents = ['pause', 'ended', 'emptied'] as const
    audioEl.addEventListener('play', handlePlay)
    stopEvents.forEach(event => audioEl.addEventListener(event, handleStop))
    setPlaying(!audioEl.paused)
    return () => {
      audioEl.removeEventListener('play', handlePlay)
      stopEvents.forEach(event => audioEl.removeEventListener(event, handleStop))
      setPlaying(false)
    }
  }, [setPlaying])

  return (
    <>
      <audio key={id} id={id} crossOrigin="anonymous" ref={audioRef} src={currentSrc} controls={false} />
      <GlobalPlayerCommandsBridge />
    </>
  )
}
